import React, { useCallback, useEffect } from 'react';
import {
    WarningModal,
    UserVideo,
    OnlineChat,
    ExamStatus,
    ExamApplicationStatus,
} from 'components';
import { IExamApplicationDetails } from 'components/models';
import { ChatMessage, IWebRTCReceiverType, VideoType } from '_helpers';
import { Button, Badge } from 'react-bootstrap';
import { ExamSessionReadyStatuses } from '../../api/ApiClient';
import styles from './OnlineModal.module.scss';

const OnlineModal = (props: IProps) => {
    const [showWarningModal, setShowWarningModal] = React.useState(false);

    const handleSwitchType = useCallback((key: VideoType) => {
        props.onSwitchType(key, props.applicantId);
    }, [props.webRTC]);

    const handleGetMessage = useCallback(() => {
        props.onGetMessages(props.applicantId);
    }, [props.applicantId]);

    useEffect(() => {
        if (props.show && props.examStatus !== ExamStatus.IsPassing) {
            props.onStart(props.applicantId);
        }
    }, [props.show]);

    const handleAdmit = React.useCallback(() => {
        props.onAdmitToExam(props.examApplicationId);
    }, [props.applicantId]);

    const handleFinishPracticeDefense = React.useCallback(() => {
        props.onFinishPracticeDefense(props.examApplicationId);
        setShowWarningModal(false);
        props.onClose();
    }, [props.applicantId]);

    const handleReadMessages = React.useCallback(() => {
        props.onReadMessages(props.applicantId);
    }, [props.applicantId]);

    return (
        <WarningModal
          title={props.title}
          show={props.show}
          onHide={props.onClose}
          dialogClassName={styles.modal}
          bodyClassNames="pb-0"
          withoutCancelButton
        >
            <div className={styles.modalBody}>
                <section className={styles.tabsContainer}>
                    <div className={styles.webCamContainer}>
                        <UserVideo
                          camSwitch
                          processing={props.webRTC && (props.webRTC.receiver.video.haveVideoStream === undefined)}
                          className={styles.video}
                          videoStream={props.webRTC && props.webRTC.receiver.video.remoteStream}
                          videoType={props.webRTC && props.webRTC.type}
                          onSwitch={handleSwitchType}
                          connectionLost={props.webRTC && props.webRTC.receiver.video.haveVideoStream === false}
                        />
                    </div>
                    <div className={styles.footer}>
                        {props.examApplicationStatus === ExamApplicationStatus.Paid && (
                            <Button className="mr-5" onClick={handleAdmit}>
                                Допустить к экзамену
                            </Button>
                        )}

                        {props.examApplicationStatus === ExamApplicationStatus.Paid && (
                            <Button
                              className="mr-5"
                              variant="outline-secondary"
                              onClick={props.onClose}
                            >
                                Отмена
                            </Button>
                        )}

                        {props.practiceDefense && props.chairman && props.examSessionReadyStatus === ExamSessionReadyStatuses.PracticalExamIsPassingAndSessionStarted ? (
                            <>
                                <Button className="mr-5" onClick={() => setShowWarningModal(true)}>
                                    Завершить практику
                                </Button>
                                <WarningModal
                                  title="Завершить практическую часть?"
                                  show={showWarningModal}
                                  onHide={() => setShowWarningModal(false)}
                                  onAccess={handleFinishPracticeDefense}
                                  accessBtnTittle="Завершить"
                                  closeBtnTittle="Отмена"
                                >
                                    <span>Нажатием на кнопку вы завершите сдачу экзамена для данного соискателя.</span>
                                </WarningModal>
                            </>
                        ) : (props.practiceDefense && props.examSessionReadyStatus === ExamSessionReadyStatuses.ExamSessionTimeout) ? (
                            <h5><Badge variant="primary">Перерыв</Badge></h5>
                        ) : (props.chairman && (props.examApplicationStatus === ExamApplicationStatus.AdmittedToExam || props.examApplicationStatus === ExamApplicationStatus.IsPassing)) ? (
                            <Button className="mr-5" variant="outline-secondary">
                                Отозвать допуск
                            </Button>
                        ) : null}
                    </div>
                </section>

                <OnlineChat
                  withTabs
                  expertId={props.expertId}
                  messages={props.messages}
                  onGetMessages={handleGetMessage}
                  onReadMessages={handleReadMessages}
                  onSendMessage={props.onSendMessage}
                  containerClassName={styles.onlineChat}
                  examApplicationDetails={props.examApplicationDetails}
                />
            </div>
        </WarningModal>
    );
};

export default OnlineModal;

interface IProps {
    show: boolean;
    examStatus: ExamStatus;
    examApplicationStatus: ExamApplicationStatus;
    examApplicationDetails?: IExamApplicationDetails | null;
    practiceDefense: boolean;
    examSessionReadyStatus?: ExamSessionReadyStatuses;
    title: string;
    messages: ChatMessage[];
    applicantId: string;
    examApplicationId: string;
    webRTC?: IWebRTC;
    expertId?: string;
    chairman: boolean;
    onStart: (applicantId: string) => void;
    onAdmitToExam: (applicantId: string) => void;
    onFinishPracticeDefense: (applicantId: string) => void;
    onSwitchType: (videoType: VideoType, applicantId: string) => void;
    onClose: () => void;
    onGetMessages: (applicantId: string) => void;
    onReadMessages: (applicantId: string) => void;
    onSendMessage: (message: string) => void;
}

interface IWebRTC {
    receiver: IWebRTCReceiverType;
    type: VideoType;
}
